import { isBrowser } from './ssr';
import { scrubPii } from './pii';
import { shouldSample } from './sampling';
import { generateEventId, getOrCreateSessionId, nowIso } from './ids';
import { buildS3Key, SAFE_APP_NAME } from './partition';
import { uploadBatch, hasCompressionStream } from './uploader';
import * as retryQueue from './retryQueue';
import { SCHEMA_VERSION } from './types';
import type {
  TelemetryConfig,
  LoggedEvent,
  EnrichedEvent,
  UserContext,
  ProjectContext,
  TelemetryDiagnostics,
} from './types';

const DEFAULT_BATCH_SIZE = 50;
const DEFAULT_FLUSH_INTERVAL_MS = 30_000;
const DEFAULT_SAMPLING_RATE = 1.0;
const MAX_EVENTS_PER_SECOND = 100;
// 5s replaces the previous 30s. Original intent was to desynchronize multi-tab
// retry storms; 5s achieves that without sitting on already-queued retries for
// half a minute after every page load.
const STARTUP_JITTER_MAX_MS = 5_000;

let config: TelemetryConfig | null = null;
let queue: EnrichedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let userCtx: Partial<UserContext> = {};
let projectCtx: Partial<ProjectContext> = {};

// Hot-path config cache: read once at init instead of `config.xxx ?? DEFAULT`
// on every logEvent. Sampling rate also pre-evaluated for the rate>=1 fast path
// (avoids one comparison per event in the dominant deployment).
let cachedBatchSize = DEFAULT_BATCH_SIZE;
let cachedSamplingRate = DEFAULT_SAMPLING_RATE;
let cachedFlushIntervalMs = DEFAULT_FLUSH_INTERVAL_MS;
let cachedDebug = false;

// Rate-limit ring buffer: O(1) insert + check, replaces O(n) Array.shift()
// loop. Slot at writePos is always the oldest of the last MAX_EVENTS_PER_SECOND
// writes; if its age is still under 1s, we're over the limit.
const rateLimitBuf = new Float64Array(MAX_EVENTS_PER_SECOND);
let rateLimitWritePos = 0;

// In-flight batches tracked here so `beforeunload` can persist them to the retry
// queue even when the in-flight `await uploadBatch` never resumes (page death).
// Idempotency is handled at the S3 key level (batchId) plus eventId dedup in
// downstream queries, so a duplicate write on race-win-then-recover is benign.
const inflightBatches = new Map<string, EnrichedEvent[]>();

let droppedBySampling = 0;
let droppedByClientRateLimit = 0;

const beforeUnloadHandler = (): void => {
  if (queue.length > 0) {
    retryQueue.flushPendingSync(queue);
    queue = [];
  }
  for (const [batchId, events] of inflightBatches) {
    retryQueue.enqueue({
      batchId,
      events,
      retryCount: 0,
      lastAttemptAt: 0,
    });
  }
  inflightBatches.clear();
};

const visibilityChangeHandler = (): void => {
  if (document.visibilityState === 'hidden') {
    void flushEvents();
    // Pause the flush interval — there's nothing else to flush until the user
    // returns. Saves wake-ups on backgrounded tabs (mobile battery cost).
    if (flushTimer) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
  } else if (document.visibilityState === 'visible') {
    // Tab regained focus — replay any retry-queue items and restart the flush
    // interval.
    void replayPendingBatches();
    if (!flushTimer && config) {
      flushTimer = setInterval(() => {
        void flushEvents();
      }, cachedFlushIntervalMs);
    }
  }
};

// Cross-tab cache invalidation: when another tab writes to localStorage, the
// `storage` event fires here and we drop our cached QueueState so the next
// read picks up the fresh data.
const storageEventHandler = (e: StorageEvent): void => {
  if (e.key === '@entourage/ecs-telemetry-module:retry-queue' || e.key === null) {
    retryQueue.invalidateCache();
  }
};

export const __testSink: EnrichedEvent[] = [];

const isTestMode = (): boolean => config?.environment === 'test';

// Eager config validation. Catches the "first flush fails 30s later for no
// visible reason" class of bugs at init time, where the consumer can actually
// see the warning in dev.
const validateConfig = (cfg: TelemetryConfig): string | null => {
  if (!cfg || typeof cfg !== 'object') return 'config must be an object';
  if (typeof cfg.bucketName !== 'string' || cfg.bucketName.trim() === '') {
    return 'bucketName must be a non-empty string';
  }
  if (typeof cfg.region !== 'string' || cfg.region.trim() === '') {
    return 'region must be a non-empty string';
  }
  if (typeof cfg.appName !== 'string' || cfg.appName.trim() === '') {
    return 'appName must be a non-empty string';
  }
  if (!SAFE_APP_NAME.test(cfg.appName)) {
    return `appName "${cfg.appName}" must match ${SAFE_APP_NAME} (Athena partition safety)`;
  }
  if (typeof cfg.authProvider !== 'function') {
    return 'authProvider must be a function returning Promise<AwsCredentials>';
  }
  return null;
};

const isKillSwitchActive = (): boolean => {
  try {
    return (
      typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_TELEMETRY_DISABLED === 'true'
    );
  } catch {
    return false;
  }
};

const checkRateLimit = (): boolean => {
  const now = Date.now();
  // Slot we'd overwrite is the oldest of the last N writes. If it's still
  // within the 1s window, we've exceeded N events/second.
  if (now - rateLimitBuf[rateLimitWritePos] < 1000) return false;
  rateLimitBuf[rateLimitWritePos] = now;
  rateLimitWritePos = (rateLimitWritePos + 1) % MAX_EVENTS_PER_SECOND;
  return true;
};

// Guards against replay storms: rapid visibility-toggle cycles or a flurry of
// visibilitychange events would otherwise invoke this concurrently, each
// reading the same ready batches and launching duplicate uploads while
// markFailed's findIndex misses concurrent callers (silent no-backoff retry).
let replayInProgress = false;

const replayPendingBatches = async (): Promise<void> => {
  if (!config) return;
  if (replayInProgress) return;
  replayInProgress = true;
  try {
    const batches = retryQueue.replay();
    for (const batch of batches) {
      try {
        const key = buildS3Key({
          appName: config.appName,
          sessionId: getOrCreateSessionId(),
          batchId: batch.batchId,
        });
        await uploadBatch(batch.events, key, config);
        retryQueue.markSucceeded(batch.batchId);
      } catch (err) {
        console.error('[ecs-telemetry] retry upload failed', batch.batchId, err);
        retryQueue.markFailed(batch.batchId);
      }
    }
  } finally {
    replayInProgress = false;
  }
};

export const initEventTracker = (cfg: TelemetryConfig): void => {
  if (isKillSwitchActive()) return;
  const invalid = validateConfig(cfg);
  if (invalid) {
    console.error(`[ecs-telemetry] init aborted: ${invalid}`);
    return;
  }
  if (config) disposeEventTracker();
  if (!isBrowser()) return;

  config = cfg;
  cachedBatchSize = cfg.batchSize ?? DEFAULT_BATCH_SIZE;
  cachedSamplingRate = cfg.samplingRate ?? DEFAULT_SAMPLING_RATE;
  cachedFlushIntervalMs = cfg.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
  cachedDebug = cfg.debug === true;

  // Debug mode bypasses the entire S3 path: no flush timer, no retry queue
  // replay, no beforeunload/visibility/storage listeners (those exist only to
  // protect against upload failures). Events go to localStorage `debugEvents`
  // for inspection in DevTools.
  if (cachedDebug) {
    console.info(
      '[ecs-telemetry] debug mode active — events are captured to localStorage ' +
      '(key: @entourage/ecs-telemetry-module:retry-queue, field: debugEvents) ' +
      'and NOT uploaded to S3.',
    );
    return;
  }

  // CompressionStream is only required for the upload path. Debug mode
  // skips uploads, so this check is debug-aware.
  if (!hasCompressionStream()) {
    console.error(
      '[ecs-telemetry] init aborted: CompressionStream is not supported by this browser. ' +
      'Minimum versions: Chrome 80, Firefox 113, Safari 16.4. Tracker is now a no-op.',
    );
    config = null;
    return;
  }

  setTimeout(() => {
    void replayPendingBatches();
  }, Math.random() * STARTUP_JITTER_MAX_MS);

  flushTimer = setInterval(() => {
    void flushEvents();
  }, cachedFlushIntervalMs);

  window.addEventListener('beforeunload', beforeUnloadHandler);
  document.addEventListener('visibilitychange', visibilityChangeHandler);
  window.addEventListener('storage', storageEventHandler);
};

export const disposeEventTracker = (): void => {
  if (queue.length > 0) {
    retryQueue.flushPendingSync(queue);
    queue = [];
  }
  // Drain in-flight batches to the retry queue. Mirrors beforeUnloadHandler —
  // dispose can be called on hot-reload or route change while uploads are
  // pending; without this drain those events vanish.
  for (const [batchId, events] of inflightBatches) {
    retryQueue.enqueue({
      batchId,
      events,
      retryCount: 0,
      lastAttemptAt: 0,
    });
  }
  inflightBatches.clear();
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  if (isBrowser()) {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
    document.removeEventListener('visibilitychange', visibilityChangeHandler);
    window.removeEventListener('storage', storageEventHandler);
  }
  config = null;
};

export const setUserContext = (ctx: UserContext): void => {
  userCtx = { ...ctx };
};

export const setProjectContext = (ctx: ProjectContext): void => {
  projectCtx = { ...ctx };
};

const enrichEvent = (event: LoggedEvent): EnrichedEvent => {
  const cfg = config!;
  return {
    ...event,
    userId: event.userId ?? userCtx.userId ?? '',
    userEmail: event.userEmail ?? userCtx.userEmail ?? '',
    yearbookId: event.yearbookId ?? projectCtx.yearbookId ?? '',
    brandId: event.brandId ?? userCtx.brandId ?? '',
    partnerId: event.partnerId ?? userCtx.partnerId ?? '',
    metadata: event.metadata ? (scrubPii(event.metadata) as Record<string, unknown>) : undefined,
    eventId: generateEventId(),
    sessionId: getOrCreateSessionId(),
    timestamp: nowIso(),
    appName: cfg.appName,
    environment: cfg.environment,
    schemaVersion: SCHEMA_VERSION,
  };
};

export const logEvent = (event: LoggedEvent): void => {
  if (!config) {
    if (typeof console !== 'undefined') {
      console.warn('[ecs-telemetry] logEvent called before initEventTracker');
    }
    return;
  }

  if (!checkRateLimit()) {
    droppedByClientRateLimit += 1;
    return;
  }

  if (!shouldSample(event, cachedSamplingRate)) {
    droppedBySampling += 1;
    return;
  }

  const enriched = enrichEvent(event);

  if (isTestMode()) {
    __testSink.push(enriched);
    return;
  }

  if (cachedDebug) {
    retryQueue.pushDebugEvent(enriched);
    return;
  }

  queue.push(enriched);
  if (queue.length >= cachedBatchSize) {
    void flushEvents();
  }
};

export const flushEvents = async (): Promise<void> => {
  if (!config || queue.length === 0) return;
  const batch = queue;
  queue = [];
  const batchId = generateEventId();
  inflightBatches.set(batchId, batch);
  const key = buildS3Key({
    appName: config.appName,
    sessionId: getOrCreateSessionId(),
    batchId,
  });
  try {
    await uploadBatch(batch, key, config);
    inflightBatches.delete(batchId);
  } catch (err) {
    // If beforeunload already consumed this batch into the retry queue, skip
    // double-enqueue here. `delete` returns false when the key was already gone.
    if (inflightBatches.delete(batchId)) {
      console.error('[ecs-telemetry] uploadBatch failed, batch queued for retry', err);
      retryQueue.enqueue({
        batchId,
        events: batch,
        retryCount: 0,
        lastAttemptAt: Date.now(),
      });
    }
  }
};

export const getDiagnostics = (): TelemetryDiagnostics => {
  const counts = retryQueue.getCounts();
  return {
    droppedBySampling,
    droppedByClientRateLimit,
    deadLettered: counts.deadLetter,
    queueSize: queue.length,
    retryQueueSize: counts.active,
  };
};

export const _resetForTests = (): void => {
  config = null;
  queue = [];
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = null;
  userCtx = {};
  projectCtx = {};
  rateLimitBuf.fill(0);
  rateLimitWritePos = 0;
  cachedBatchSize = DEFAULT_BATCH_SIZE;
  cachedSamplingRate = DEFAULT_SAMPLING_RATE;
  cachedFlushIntervalMs = DEFAULT_FLUSH_INTERVAL_MS;
  cachedDebug = false;
  inflightBatches.clear();
  retryQueue.invalidateCache();
  droppedBySampling = 0;
  droppedByClientRateLimit = 0;
  __testSink.length = 0;
};
